import { CircularProgress } from '@mui/material'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useGetPriceListHistoryQuery,
  useUpdateProjectUserMutation,
} from 'apiv2'
import { groupRoleByUserId } from 'helpers/groupRoleByUserId'
import { useProjectPermissions } from 'hooks/useProjectPermissions'
import { useProjectUserStatus } from 'hooks/useProjectUserStatus'
import { closeUserRoleModal, userRoleModalSelector } from 'store/modals'
import {
  getProjectInfoAction,
  clearProjectInfo,
  getProjectUsersAction,
} from 'store/projects'
import ModalBasic from 'ui/components/modals/ModalBasic/'
import { getProjectUsersByStatus } from 'ui/components/UserStatusControl/helpers'
import { SelectNewUser } from './components/SelectNewUser'
import { SelectUser } from './components/SelectUser'
import {
  usePreviousSlug,
  useRoleList,
  useUserList,
  usePriceList,
} from './hooks'
import { LoadingContainer, UsersSection, UsersSectionTitle } from './styled'

const ModalUserRole = () => {
  const modalInfo = useSelector(userRoleModalSelector)
  const title = modalInfo?.title
  const open = modalInfo?.open

  const dispatch = useDispatch()
  const router = useRouter()

  const projectSlug = router.query.projectSlug || modalInfo?.projectSlug

  const {
    changingUserId,
    handleDeactivateProjectUser,
    handleActivateProjectUser,
  } = useProjectUserStatus({ slug: projectSlug })

  const handlerClose = () => {
    dispatch(closeUserRoleModal())
    dispatch(getProjectInfoAction(projectSlug))
    dispatch(getProjectUsersAction(projectSlug))
  }

  useEffect(() => {
    return () => dispatch(clearProjectInfo())
  }, [])

  const { projectInfo, isProjectInfoLoading, isSuccess, canEditProject } =
    useProjectPermissions(projectSlug)

  const [userList, updateUserList] = useUserList({
    open,
    users: projectInfo?.users,
  })

  const canEditProjectUsers = !projectInfo?.is_archived || canEditProject

  const roleList = useRoleList({ open })

  const { data: estimateHistory } = useGetPriceListHistoryQuery(projectSlug, {
    skip: !projectSlug,
  })

  const priceList = usePriceList({ open })

  const previousSlug = usePreviousSlug(isSuccess, projectSlug)

  const [updateProjectUserMutation] = useUpdateProjectUserMutation()

  const isFullData = roleList && projectInfo && userList && priceList

  const hasEstimate = !!estimateHistory?.current_details.length

  const { activeUsers, inactiveUsers, activeManagersCount } =
    getProjectUsersByStatus(groupRoleByUserId(projectInfo?.users || []))

  return (
    <ModalBasic
      size="max"
      open={open}
      handlerClose={handlerClose}
      title={title}
      onRouteChangeClose={handlerClose}
    >
      {!isFullData && isProjectInfoLoading && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {isFullData && !isProjectInfoLoading && previousSlug === projectSlug && (
        <div>
          <UsersSection>
            {activeUsers.length > 0 && (
              <>
                <UsersSectionTitle>Активные</UsersSectionTitle>

                <SelectUser
                  users={activeUsers}
                  userList={userList}
                  roleList={roleList}
                  priceList={priceList}
                  hasEstimate={hasEstimate}
                  updateProjectUserMutation={updateProjectUserMutation}
                  slug={projectSlug}
                  canEditProjectUsers={canEditProjectUsers}
                  activeManagersCount={activeManagersCount}
                  changingUserId={changingUserId}
                  onStatusChange={handleDeactivateProjectUser}
                />
              </>
            )}

            {canEditProjectUsers && (
              <SelectNewUser
                userList={userList}
                roleList={roleList}
                priceList={priceList}
                hasEstimate={hasEstimate}
                updateProjectUserMutation={updateProjectUserMutation}
                updateUserList={updateUserList}
                slug={projectSlug}
              />
            )}
          </UsersSection>

          {inactiveUsers.length > 0 && (
            <UsersSection>
              <UsersSectionTitle>Неактивные</UsersSectionTitle>

              <SelectUser
                users={inactiveUsers}
                userList={userList}
                roleList={roleList}
                priceList={priceList}
                hasEstimate={hasEstimate}
                updateProjectUserMutation={updateProjectUserMutation}
                slug={projectSlug}
                canEditProjectUsers={canEditProjectUsers}
                activeManagersCount={activeManagersCount}
                changingUserId={changingUserId}
                onStatusChange={handleActivateProjectUser}
              />
            </UsersSection>
          )}
        </div>
      )}
    </ModalBasic>
  )
}

const WrapperUserRole = () => {
  const modalInfo = useSelector(userRoleModalSelector)
  const open = modalInfo?.open
  const slug = modalInfo?.projectSlug

  useEffect(() => {
    if (slug) {
      sessionStorage.setItem('slug', slug)
    }
  }, [slug])

  if (!open) {
    return null
  }

  return <ModalUserRole />
}

export default WrapperUserRole
